import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Divider,
  Link as MuiLink,
  Fab,
  Autocomplete,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  ExpandMore,
  Launch,
  Description,
  Visibility,
  LibraryBooks
} from '@mui/icons-material';
import apiClient from '../utils/axiosConfig';

const LinkLibrary = () => {
  const [links, setLinks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    category: '',
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [user, setUser] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchLinks();
    fetchCategories();
    checkUserRole();
    // Set default to show all categories
    setSelectedCategory('');
  }, []);

  const checkUserRole = async () => {
    try {
      const response = await apiClient.get('/api/verify-token');
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to verify user:', error);
    }
  };

  const fetchLinks = async (isSearch = false) => {
    try {
      setError('');
      if (isSearch) {
        setSearchLoading(true);
      }
      
      const params = new URLSearchParams();
      
      // Input validation
      const validSearchTerm = searchTerm.trim().substring(0, 100);
      if (selectedCategory) params.append('category', selectedCategory);
      if (validSearchTerm) params.append('search', validSearchTerm);
      
      const response = await apiClient.get(`/api/links?${params}`);
      
      // Validate response
      if (response.data && Array.isArray(response.data)) {
        setLinks(response.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching links:', error);
      setError(error.response?.data?.error || 'Failed to load links. Please try again.');
      setLinks([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/api/links/categories');
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleSearch = () => {
    if (searchTerm.trim().length > 100) {
      setError('Search term must be less than 100 characters');
      return;
    }
    fetchLinks(true);
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length <= 100) {
        fetchLinks(true);
      }
    }, searchTerm ? 300 : 0);
    
    return () => clearTimeout(timeoutId);
  }, [selectedCategory, searchTerm]);

  const handleLinkClick = async (linkId) => {
    try {
      await apiClient.patch(`/api/links/${linkId}/click`);
    } catch (error) {
      console.error('Failed to track click:', error);
    }
  };

  const handleAddLink = () => {
    setEditingLink(null);
    setFormData({ title: '', url: '', category: '', description: '' });
    setError('');
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError('');
    setSubmitLoading(false);
  };

  const handleEditLink = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url || '',
      category: link.category,
      description: link.description || ''
    });
    setDialogOpen(true);
  };

  const handleDeleteLink = async (linkId, linkTitle) => {
    if (window.confirm(`Are you sure you want to delete "${linkTitle}"? This action cannot be undone.`)) {
      setDeleteLoading(linkId);
      setError('');
      
      try {
        await apiClient.delete(`/api/links/${linkId}`);
        setSuccess('Link deleted successfully');
        fetchLinks();
        fetchCategories();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
        
      } catch (error) {
        console.error('Error deleting link:', error);
        setError(error.response?.data?.error || 'Failed to delete link. Please try again.');
      } finally {
        setDeleteLoading(null);
      }
    }
  };

  const handleSaveLink = async () => {
    setSubmitLoading(true);
    setError('');
    
    try {
      // Input validation
      const trimmedData = {
        title: formData.title.trim(),
        url: formData.url.trim(),
        category: formData.category.trim(),
        description: formData.description.trim()
      };
      
      // Validate required fields
      if (!trimmedData.title || trimmedData.title.length < 2) {
        setError('Title must be at least 2 characters long');
        return;
      }
      
      if (!trimmedData.category || trimmedData.category.length < 2) {
        setError('Category must be at least 2 characters long');
        return;
      }
      
      if (!trimmedData.url) {
        setError('URL is required');
        return;
      }
      
      // Validate URL format
      try {
        new URL(trimmedData.url);
      } catch {
        setError('Please enter a valid URL (e.g., https://example.com)');
        return;
      }
      
      // Length validation
      if (trimmedData.title.length > 200) {
        setError('Title must be less than 200 characters');
        return;
      }
      
      if (trimmedData.category.length > 100) {
        setError('Category must be less than 100 characters');
        return;
      }
      
      if (trimmedData.description.length > 5000) {
        setError('Description must be less than 5000 characters');
        return;
      }
      
      if (editingLink) {
        await apiClient.put(`/api/links/${editingLink.id}`, trimmedData);
        setSuccess('Link updated successfully');
      } else {
        await apiClient.post('/api/links', trimmedData);
        setSuccess('Link created successfully');
      }
      
      setDialogOpen(false);
      fetchLinks();
      fetchCategories();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error saving link:', error);
      setError(error.response?.data?.error || 'Failed to save link. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const groupedLinks = links.reduce((acc, link) => {
    if (!acc[link.category]) {
      acc[link.category] = [];
    }
    acc[link.category].push(link);
    return acc;
  }, {});

  const isAdmin = user?.role === 'admin';

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LibraryBooks sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Link Library
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddLink}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Add Link
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} sx={{
          alignItems: "center"
        }}>
          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <TextField
              fullWidth
              label="Search links..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value.substring(0, 100);
                setSearchTerm(value);
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              size="small"
              helperText={searchTerm.length > 80 ? `${searchTerm.length}/100 characters` : ''}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              slotProps={{
                input: {
                  endAdornment: searchLoading && (
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                  )
                },

                htmlInput: { maxLength: 100 }
              }} />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Category</InputLabel>
              <Select
                value={selectedCategory || ''}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setLoading(true);
                }}
                label="Category"
                displayEmpty
                notched
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 2
            }}>
            <Button
              variant="outlined"
              startIcon={searchLoading ? <CircularProgress size={16} /> : <Search />}
              onClick={handleSearch}
              disabled={searchLoading}
              fullWidth
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Links Display */}
      {Object.keys(groupedLinks).length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{
            color: "text.secondary"
          }}>
            No links found
          </Typography>
          <Typography sx={{
            color: "text.secondary"
          }}>
            {searchTerm || selectedCategory ? 'Try adjusting your search criteria' : 'No links available'}
          </Typography>
        </Paper>
      ) : (
        Object.entries(groupedLinks).map(([category, categoryLinks]) => (
          <Accordion key={category} defaultExpanded sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {category}
                </Typography>
                <Chip label={`${categoryLinks.length} items`} size="small" />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={1.5}>
                {categoryLinks.map((link) => (
                  <Grid
                    key={link.id}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                      lg: 3
                    }}>
                    <Tooltip
                      title={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {link.title}
                          </Typography>
                          {link.description && (
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                              {link.description}
                            </Typography>
                          )}
                        </Box>
                      }
                      arrow
                      placement="top"
                      enterDelay={500}
                    >
                      <Card sx={{ 
                        height: 'auto',
                        minHeight: '75px',
                        maxHeight: '85px',
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 1.5,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        transition: 'all 0.2s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: '0 3px 12px rgba(0,0,0,0.12)',
                          transform: 'translateY(-1px)',
                          borderColor: 'primary.main'
                        }
                      }}>
                      <CardContent sx={{ 
                        flexGrow: 1, 
                        p: 0.75, 
                        pb: 0.25,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        {/* Top section with title and admin actions */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="h6" component="h3" sx={{ 
                              fontWeight: 600, 
                              fontSize: '0.85rem',
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical'
                            }}>
                              {link.title}
                            </Typography>
                            {isAdmin && (
                              <Box sx={{ display: 'flex', gap: 0.25, ml: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditLink(link)}
                                  sx={{ p: 0.25, fontSize: '0.75rem' }}
                                >
                                  <Edit fontSize="inherit" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteLink(link.id, link.title)}
                                  disabled={deleteLoading === link.id}
                                  sx={{ p: 0.25, fontSize: '0.75rem' }}
                                >
                                  <Delete fontSize="inherit" />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                          {link.description && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "text.secondary",
                                fontSize: '0.7rem',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: 'vertical'
                              }}>
                              {link.description}
                            </Typography>
                          )}
                        </Box>
                        
                        {/* Bottom section with Open/View button and view count */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <Visibility sx={{ fontSize: '10px' }} color="action" />
                            <Typography
                              variant="caption"
                              sx={{
                                color: "text.secondary",
                                fontSize: '0.55rem'
                              }}>
                              {link.click_count || 0}
                            </Typography>
                          </Box>
                          {link.url ? (
                            <Button
                              size="small"
                              startIcon={<Launch />}
                              onClick={() => {
                                handleLinkClick(link.id);
                                window.open(link.url, '_blank');
                              }}
                              sx={{ 
                                fontSize: '0.65rem',
                                minHeight: 'auto',
                                py: 0.25,
                                px: 0.75,
                                borderRadius: 1
                              }}
                            >
                              Open
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              startIcon={<Visibility />}
                              onClick={() => handleLinkClick(link.id)}
                              sx={{ 
                                fontSize: '0.65rem',
                                minHeight: 'auto',
                                py: 0.25,
                                px: 0.75,
                                borderRadius: 1
                              }}
                            >
                              View
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                    </Tooltip>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Add/Edit Link Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLink ? 'Edit Link' : 'Add New Link'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => {
                  const value = e.target.value.substring(0, 200);
                  setFormData({ ...formData, title: value });
                }}
                required
                size="small"
                helperText={formData.title.length > 180 ? `${formData.title.length}/200 characters` : 'Required'}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                slotProps={{
                  htmlInput: { maxLength: 200 }
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="URL"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value.trim() })}
                placeholder="https://example.com"
                required
                size="small"
                type="url"
                helperText="Required - Must be a valid URL starting with http:// or https://"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid size={12}>
              <Autocomplete
                fullWidth
                freeSolo
                options={categories}
                value={formData.category}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, category: newValue || '' });
                }}
                onInputChange={(event, newInputValue) => {
                  setFormData({ ...formData, category: newInputValue });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    required
                    size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    helperText={`Required - Select existing or create new (${formData.category.length}/100 chars)`}
                    slotProps={{
                      ...params.slotProps,
                      htmlInput: { ...params.slotProps.htmlInput, maxLength: 100 }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => {
                  const value = e.target.value.substring(0, 5000);
                  setFormData({ ...formData, description: value });
                }}
                multiline
                rows={6}
                placeholder="Enter detailed description or document content..."
                helperText={`Optional - ${formData.description.length}/5000 characters`}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                slotProps={{
                  htmlInput: { maxLength: 5000 }
                }}
              />
            </Grid>
          </Grid>
          
          {error && (
            <Grid size={12}>
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            disabled={submitLoading}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveLink}
            disabled={submitLoading || !formData.title.trim() || !formData.category.trim() || !formData.url.trim()}
            sx={{ 
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 3
            }}
          >
            {submitLoading ? 'Saving...' : (editingLink ? 'Update Link' : 'Create Link')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LinkLibrary;